import { map } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnInit, Output, Type } from '@angular/core';
import { RegepTechnicalResourceService } from './regep-technical-resource.service';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { ConnectionObjectInfo } from './regep-technical-resource.models';
import { RegepApplicationService } from '../../services/regep-application.service';
import { EegEnergyProducer } from '../../entities/regep-eeg-energy-producer';
import { RegepOrderProcessService } from '../../services/regep-order-process.service';
import { RegepOrderProcess } from '../../entities/regep-order-process';
import { generateIdentifier } from './regep-technical-resource-generator.lsw';
import { EnvironmentService } from 'src/app/services/environment.service';
import { ErrorService } from 'src/app/services/error/error.service';

type EegProducerIdControlType = 'input' | 'text';

@Component({
  selector: 'app-regep-technical-resource',
  templateUrl: './regep-technical-resource.component.html',
  styleUrls: ['./regep-technical-resource.component.scss'],
})
export class RegepTechnicalResourceComponent extends DestroyableComponent implements OnInit {
  @Input() isReadOnly: boolean;
  @Input() isEdit: boolean;
  @Input() applicationId: number;
  @Input() orderProcess: RegepOrderProcess;
  @Input() disableEegProducerId: boolean;

  @Output() formChange = new EventEmitter<FormGroup>();

  isLoading: boolean = true;
  technicalResourcesForm: FormGroup;
  showEnergyProducerIdInfoText: boolean = this.environmentService.showEnergyProducerIdInfoText;

  private connectionObjectInfo: ConnectionObjectInfo;
  private eegProducerIdGenerator = this.environmentService.eegProducerIdGenerator;
  public eegProducerIdControlType: EegProducerIdControlType = this.environmentService.eegProducerIdGenerator === 'lsw' ? 'input' : 'text';

  constructor(
    private orderProcessService: RegepOrderProcessService,
    private regepApplicationService: RegepApplicationService,
    private regepTechnicalResourceService: RegepTechnicalResourceService,
    public technicalResourceService: RegepTechnicalResourceService,
    private environmentService: EnvironmentService,
    private errorService: ErrorService
  ) {
    super();
  }

  ngOnInit(): void {
    this.preventLeak(this.regepApplicationService.getApplication(this.applicationId)).subscribe(
      application => {
        this.connectionObjectInfo = {
          postCode: application.connectionobject.postcode,
        };

        this.buildForm(application.eeg_energy_producers);
      },
      error => {},
      () => (this.isLoading = false)
    );
  }

  addTechnicalResource() {
    const technicalResources = this.technicalResourcesForm.controls.technicalResources as FormArray;
    technicalResources.push(this.initTechnicalResource());
    this.technicalResourcesForm.markAsDirty();
  }

  deleteTechnicalResource(index: number) {
    const technicalResources = this.technicalResourcesForm.controls.technicalResources as FormArray;
    technicalResources.removeAt(index);
  }

  isLastTechnicalResource(index: number) {
    return index === (this.technicalResourcesForm.controls.technicalResources as FormArray).length - 1;
  }

  isTechnicalResourceNotSaved(index: number) {
    return !this.technicalResourcesForm.controls.technicalResources.value[index].id;
  }

  technicalResourceUpdated(input: any, index: number) {
    this.technicalResourceService.setEegEnergyProducers(this.technicalResourcesForm.value.technicalResources);
    this.technicalResourceService.setFormState(this.technicalResourcesForm);
  }

  rowTrackByFunction(_, item: EegEnergyProducer) {
    return item.id;
  }

  saveEnergyProducerIds(orderProcess: RegepOrderProcess) {
    this.preventLeak(
      this.orderProcessService.saveEegEnergyProducers(orderProcess.id, this.technicalResourcesForm.controls.technicalResources.value)
    ).subscribe(
      results => {
        const eegEnergyProducers = results.map(r => ({
          id: r.id,
          energy_producer_id: r.energyProducerId,
          master_id: r.masterId,
        }));
        this.technicalResourcesForm.controls.technicalResources.reset(eegEnergyProducers);
        this.regepTechnicalResourceService.setEegEnergyProducers(eegEnergyProducers);
        this.technicalResourceService.setFormState(this.technicalResourcesForm);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  trackByFn(index, item) {
    return index;
  }

  private buildForm(eegEnergyProducers: EegEnergyProducer[]) {
    const technicalResources = [];

    if (eegEnergyProducers?.length) {
      for (const energyProducer of eegEnergyProducers) {
        technicalResources.push(
          new FormGroup({
            id: new FormControl(energyProducer.id),
            energy_producer_id: new FormControl(energyProducer.energy_producer_id, [Validators.required]),
            master_id: new FormControl(energyProducer.master_id, this.isEdit ? [Validators.required, this.masterIdValidator()] : []),
          })
        );
      }
    } else {
      technicalResources.push(this.initTechnicalResource());
    }

    this.technicalResourcesForm = new FormGroup({
      technicalResources: new FormArray(technicalResources, [Validators.required, this.uniqueIdValidator()]),
    });

    this.onFormChange(this.technicalResourcesForm.value);

    this.preventLeak(this.technicalResourcesForm.valueChanges).subscribe(value => {
      this.onFormChange(value);
    });
  }

  private onFormChange(value) {
    this.technicalResourceService.setEegEnergyProducers(this.technicalResourcesForm.controls.technicalResources.value);
    this.technicalResourceService.setFormState(this.technicalResourcesForm);
    this.formChange.emit(this.technicalResourcesForm);
  }

  private initTechnicalResource() {
    return new FormGroup({
      id: new FormControl(0),
      energy_producer_id: new FormControl(this.getNextIdentifier(), [Validators.required]),
      master_id: new FormControl(''),
    });
  }

  private getNextIdentifier() {
    if (this.eegProducerIdGenerator === 'lsw') {
      const technicalResources = this.technicalResourcesForm?.controls.technicalResources as FormArray;
      return generateIdentifier(this.applicationId, technicalResources?.length ?? 0, this.connectionObjectInfo?.postCode);
    } else {
      return '';
    }
  }

  private masterIdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const masterIdRegex = new RegExp('^SEE\\d{12}$');
      if (!masterIdRegex.test(control.value)) {
        return { masterIdError: true };
      }
      return null;
    };
  }
  private uniqueIdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const energyProducerIds = control.value.map(c => c.energy_producer_id);
      if (!this.hasUniqueValues(energyProducerIds)) {
        return { uniqueIdError: true };
      }
      return null;
    };
  }

  // check if array has unique elements
  private hasUniqueValues(arr) {
    return new Set(arr).size === arr.length;
  }
}
